import { h } from 'preact'
import { useEffect } from 'preact/hooks'
import { ConversationRow } from './ConversationRow.jsx'
import { listWidgetConversations, getWidgetEmailThread } from '../beacon-api.js'
import { t } from '../beacon-i18n.js'

/**
 * ConversationsScreen — mixed Email + AI(source=beacon) list from
 * GET /support/widget/conversations (see plan "Support widget API").
 * Clicking a row navigates to the matching detail screen:
 *   email → EmailThreadScreen (fetches the full thread)
 *   ai    → AiScreen (continues the stored agent_id)
 */
export function ConversationsScreen({ store }) {
  useEffect(() => {
    let cancelled = false
    store.conversationsLoading.value = true
    store.conversationsError.value = false

    listWidgetConversations()
      .then((list) => {
        if (!cancelled) store.setConversations(list)
      })
      .catch(() => {
        if (!cancelled) store.conversationsError.value = true
      })
      .finally(() => {
        if (!cancelled) store.conversationsLoading.value = false
      })

    return () => { cancelled = true }
  }, [])

  async function handleRowClick(conversation) {
    if (conversation.type === 'ai') {
      // AiScreen continues a conversation via its agent_id (see AiController#stream /
      // Api::Ai::Conversations::MessagesController), not the Ai::Conversation DB id.
      store.openAiConversation(conversation.agent_id)
      return
    }

    try {
      const thread = await getWidgetEmailThread(conversation.id)
      store.openEmailThread(thread)
    } catch {
      store.conversationsError.value = true
    }
  }

  const loading = store.conversationsLoading.value
  const error   = store.conversationsError.value
  const list    = store.conversations.value

  return (
    <div class="beacon-conversations-screen">
      {loading && <div class="beacon-conversations-loading">{t('support.beacon.conversations.loading')}</div>}

      {!loading && error && (
        <div class="beacon-conversations-error">
          <i class="bi bi-exclamation-triangle-fill"></i> {t('support.beacon.conversations.error')}
        </div>
      )}

      {!loading && !error && list.length === 0 && (
        <div class="beacon-conversations-empty">
          <i class="bi bi-chat-square-text"></i>
          <p>{t('support.beacon.conversations.empty')}</p>
        </div>
      )}

      {!loading && !error && list.length > 0 && (
        <ul class="beacon-conversations-list">
          {list.map((conversation) => (
            <li key={`${conversation.type}-${conversation.id}`}>
              <ConversationRow conversation={conversation} onClick={() => handleRowClick(conversation)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
